import MarkerDisplayManager from '../managers/markerDisplayManager.js';
import { layerManager } from '../managers/layerManager.js';
import { storeEvents, EVENTS } from '../../../common/stores/storeEvents.js';
import { calculateDynamicZoom, calculatePadding } from '../utils/mapUtils.js';
import { logger } from '../../../utils/debug/logger.js';

/**
 * Crée un marqueur de cluster (pastille ronde avec le compte) — factorise les
 * 3 implémentations quasi identiques des calques (seules la couleur, la classe,
 * la taille et le z-index variaient).
 *
 * Fonction de module (et non méthode) à dessein : `createClusterMarker` est passé
 * en callback à initializeCluster, parfois NON lié — une méthode utilisant `this`
 * planterait. Cette fonction n'a aucune dépendance à `this`.
 *
 * @param {Object} opts
 * @param {number} opts.count - Nombre d'éléments regroupés
 * @param {google.maps.LatLng} opts.position - Position du cluster
 * @param {string} opts.color - Couleur de fond
 * @param {string} [opts.className='cluster-marker'] - Classe CSS du conteneur
 * @param {number} [opts.size=40] - Diamètre en px
 * @param {number} [opts.zIndex] - z-index optionnel
 * @returns {google.maps.marker.AdvancedMarkerElement}
 */
export function createClusterBadge({ count, position, color, className = 'cluster-marker', size = 40, zIndex }) {
    const div = document.createElement('div');
    div.className = className;
    div.style.cssText = `
        background: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 2px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    `;
    div.textContent = count;

    const options = { position, content: div };
    if (zIndex !== undefined) options.zIndex = zIndex;
    return new google.maps.marker.AdvancedMarkerElement(options);
}

/**
 * Classe de base pour les stores de calques cartographiques
 * Abstrait les fonctionnalités communes à tous les calques
 */
class BaseLayerStore {
    constructor(layerName) {
        this.layerName = layerName;
        // Standardisation : par défaut, le nom de la couche de marqueurs est identique au nom du calque
        this.markerLayerName = layerName;
        this.markerDisplayManager = new MarkerDisplayManager();
        this.map = null;
        this.disposers = new Set();

        // Écouter les changements de visibilité du calque
        const layerChangeDisposer = storeEvents.subscribe(
            EVENTS.VISUALIZATIONS.MAP.LAYERS.CHANGED,
            (data) => {
                if (data.layer === this.layerName) {
                    this.applyVisibility(data.state);
                }
            }
        );

        this.disposers.add(layerChangeDisposer);
    }

    /**
 * Initialise le store avec une instance de Google Maps
 * @param {google.maps.Map} map - Instance Google Maps
 * @returns {boolean} - Statut de l'initialisation
 */
    initialize(map) {
        // Vérifier que la carte est valide
        if (!map || !(map instanceof google.maps.Map)) {
            logger.map.stores.error(`❌ Instance de carte invalide pour ${this.layerName}Store`);
            return false;
        }

        logger.map.stores.info(`🚀 Initialisation de ${this.layerName}Store`);
        this.map = map;

        try {
            // Initialiser le cluster s'il ne l'est pas déjà
            if (!this.markerDisplayManager.isInitialized()) {
                if (!this.createClusterMarker || typeof this.createClusterMarker !== 'function') {
                    logger.map.error(`❌ Méthode createClusterMarker manquante dans ${this.constructor.name}`);
                    return false;
                }

                this.markerDisplayManager.initializeCluster(map, this.createClusterMarker.bind(this));
            }

            return true;
        } catch (error) {
            logger.map.error(`❌ Erreur lors de l'initialisation de ${this.layerName}Store:`, error);
            return false;
        }
    }

    /**
     * Méthode abstraite pour créer un marqueur de cluster
     * @param {Object} params - Paramètres du cluster
     */
    createClusterMarker(params) {
        throw new Error('createClusterMarker doit être implémenté par les classes dérivées');
    }

    /**
     * Change la visibilité du calque
     * @param {boolean} visible - État de visibilité
     */
    toggleVisibility(visible) {
        storeEvents.emit(EVENTS.VISUALIZATIONS.MAP.LAYERS.CHANGED, {
            layer: this.layerName,
            state: visible
        });
    }

    /**
     * Centre la carte sur tous les marqueurs du calque avec un zoom dynamique
     * Méthode générique utilisée par tous les stores enfants pour un comportement cohérent
     * @param {Object} options - Options de centrage (optionnel)
     * @param {number} options.minZoom - Zoom minimal (par défaut: aucune limite)
     * @param {boolean} options.forceResize - Forcer un resize avant centrage (par défaut: false)
     */
    centerMapOnMarkers(options = {}) {
        if (!this.map) {
            logger.map.stores.warn(`❌ Carte non initialisée pour ${this.layerName}`);
            return;
        }

        const bounds = this.getBounds();
        if (!bounds) {
            logger.map.warn(`⚠️ Impossible de centrer la carte : aucun marqueur ${this.layerName} disponible`);
            return;
        }

        // Forcer un resize si demandé
        if (options.forceResize) {
            google.maps.event.trigger(this.map, 'resize');
        }

        // Utiliser les fonctions utilitaires standardisées.
        // getDiv() peut renvoyer undefined si le SDK Google Maps n'a pas pu se
        // charger (ex. clé API restreinte par référent sur un domaine non autorisé) :
        // dans ce cas on abandonne le centrage au lieu de crasher sur offsetHeight.
        const mapDiv = this.map.getDiv();
        if (!mapDiv) {
            logger.map.stores.warn(`❌ Conteneur de carte indisponible pour ${this.layerName} (Google Maps non chargé ?)`);
            return;
        }
        const maxZoom = this.calculateDynamicZoom(mapDiv.offsetHeight);
        const padding = this.calculatePadding(mapDiv);

        logger.map.debug(`🔍 Centrage ${this.layerName} avec zoom max dynamique: ${maxZoom}`);

        // Appliquer les limites géographiques avec padding
        this.map.fitBounds(bounds, padding);

        // Limiter le zoom après l'ajustement automatique
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            const currentZoom = this.map.getZoom();
            logger.map.debug(`🔍 Zoom après ajustement pour ${this.layerName}: ${currentZoom} (max: ${maxZoom})`);

            if (currentZoom > maxZoom) {
                logger.map.debug(`🔍 Limitation du zoom à ${maxZoom}`);
                this.map.setZoom(maxZoom);
            } else if (options.minZoom && currentZoom < options.minZoom) {
                logger.map.debug(`🔍 Augmentation du zoom à ${options.minZoom}`);
                this.map.setZoom(options.minZoom);
            }
        });
    }

    /**
     * Méthodes utilitaires pour le calcul de zoom et padding
     * Utilisent les fonctions standardisées de mapUtils.js
     */
    calculateDynamicZoom(containerHeight) {
        return calculateDynamicZoom(containerHeight);
    }

    calculatePadding(mapDiv) {
        return calculatePadding(mapDiv);
    }

    /**
 * Obtient les limites géographiques de tous les marqueurs du calque
 * Méthode mutualisée qui inclut tous les marqueurs, même ceux masqués par un clusterer
 * @returns {google.maps.LatLngBounds|null} Limites géographiques ou null
 */
    getBounds() {
        if (!this.markerDisplayManager) return null;

        const bounds = new google.maps.LatLngBounds();
        let hasMarkers = false;

        // Récupérer les marqueurs de la couche principale
        const layerMarkers = this.markerDisplayManager.layers.get(this.markerLayerName);

        if (layerMarkers && layerMarkers.size > 0) {
            logger.map.debug(`📊 Calcul des bounds pour ${layerMarkers.size} marqueurs de ${this.layerName}`);

            // Parcourir TOUS les marqueurs, peu importe s'ils sont visibles ou masqués par un cluster
            layerMarkers.forEach((marker, key) => {
                if (marker && marker.position) {
                    bounds.extend(marker.position);
                    hasMarkers = true;
                }
            });

            logger.map.debug(`✅ Bounds calculés incluant ${hasMarkers ? 'tous les' : 'aucun'} marqueurs pour ${this.layerName}`);
        } else {
            logger.map.debug(`⚠️ Aucun marqueur trouvé pour la couche ${this.markerLayerName}`);
        }

        // Si aucun marqueur trouvé et que nous sommes dans un calque spécifique,
        // on peut essayer avec les autres couches
        if (!hasMarkers) {
            this.markerDisplayManager.layers.forEach((markers, layerName) => {
                if (layerName !== this.markerLayerName) { // Éviter la duplication
                    markers.forEach(marker => {
                        if (marker && marker.position) {
                            bounds.extend(marker.position);
                            hasMarkers = true;
                        }
                    });
                }
            });
        }

        return hasMarkers ? bounds : null;
    }

    /**
 * Applique l'état de visibilité aux marqueurs
 * @param {boolean} visible - État de visibilité
 */
    applyVisibility(visible) {
        if (!this.map) return;

        if (visible) {
            // 1. Préparation du calque avant affichage (hook pour la classe dérivée)
            this.prepareLayerBeforeShow();

            // 2. S'assurer que le cluster est bien initialisé
            if (!this.markerDisplayManager.isInitialized()) {
                this.markerDisplayManager.initializeCluster(this.map, this.createClusterMarker.bind(this));
            }

            // 3. Préparer/rafraîchir les marqueurs si nécessaire (hook pour la classe dérivée)
            this.updateLayerMarkers();

            // 4. Rendre les marqueurs visibles
            const layerMarkers = this.markerDisplayManager.layers.get(this.markerLayerName);
            if (layerMarkers) {
                layerMarkers.forEach(marker => {
                    marker.map = this.map;
                });
            }

            // 5. Ajouter les marqueurs au cluster avec délai configurable
            const config = layerManager.getLayerConfig(this.layerName);
            const delay = config ? config.clusterDelay : 0;

            if (delay > 0) {
                setTimeout(() => {
                    logger.map.debug(`📍 Ajout des marqueurs au cluster après délai (${delay}ms)`);
                    this.markerDisplayManager.addMarkersToCluster(this.map);

                    // 5.1 Si c'est le calque familial, initialiser les bounds pour optimiser le centrage futur
                    if (this.layerName === 'family' && typeof this.initializeMapBounds === 'function') {
                        logger.map.debug('🗺️ Initialisation des bounds pour le calque familial');
                        this.initializeMapBounds();
                    }

                    // Centrage différé après que les marqueurs ont été ajoutés
                    setTimeout(() => {
                        // 6. Actions post-affichage (hook pour la classe dérivée)
                        this.afterLayerShown();
                    }, 100);
                }, delay);
            } else {
                logger.map.debug('📍 Ajout des marqueurs au cluster sans délai');
                this.markerDisplayManager.addMarkersToCluster(this.map);

                // 5.1 Si c'est le calque familial, initialiser les bounds pour optimiser le centrage futur
                if (this.layerName === 'family' && typeof this.initializeMapBounds === 'function') {
                    logger.map.debug('🗺️ Initialisation des bounds pour le calque familial');
                    this.initializeMapBounds();
                }

                // Centrage différé après que les marqueurs ont été ajoutés
                setTimeout(() => {
                    // 6. Actions post-affichage (hook pour la classe dérivée)
                    this.afterLayerShown();
                }, 100);
            }
        } else {
            logger.map.debug(`🔍 Désactivation du calque ${this.layerName}`);
            this.markerDisplayManager.toggleLayerVisibility(this.markerLayerName, false, this.map);

            // 7. Actions après masquage (hook pour la classe dérivée)
            this.afterLayerHidden();
        }
    }

    /**
     * Hook: Préparations avant affichage du calque
     * À surcharger par les classes dérivées si nécessaire
     */
    prepareLayerBeforeShow() {
        // Implémentation par défaut vide
    }

    /**
     * Hook: Mise à jour des marqueurs du calque
     * À surcharger par les classes dérivées si nécessaire
     */
    updateLayerMarkers() {
        // Implémentation par défaut vide
    }

    /**
     * Hook: Actions après affichage du calque
     * À surcharger par les classes dérivées si nécessaire
     */
    afterLayerShown() {
        // Implémentation par défaut vide
    }

    /**
     * Hook: Actions après masquage du calque
     * À surcharger par les classes dérivées si nécessaire
     */
    afterLayerHidden() {
        // Implémentation par défaut vide
    }


    /**
     * Indique si au moins un marqueur (tous calques confondus) est actuellement
     * affiché sur la carte (marker.map !== null).
     * @returns {boolean}
     */
    hasActiveMarkers() {
        if (!this.markerDisplayManager) return false;
        let hasMarkers = false;
        this.markerDisplayManager.layers.forEach(layerMarkers => {
            layerMarkers.forEach(marker => {
                if (marker.map !== null) {
                    hasMarkers = true;
                }
            });
        });
        return hasMarkers;
    }

    /**
 * Nettoie les ressources utilisées par le store
 */
    cleanup() {
        logger.map.debug(`🧹 Nettoyage des ressources pour ${this.layerName}Store`);

        // 1. Nettoyage des marqueurs et de leurs écouteurs
        if (this.markerDisplayManager) {
            const layerMarkers = this.markerDisplayManager.layers.get(this.markerLayerName);
            if (layerMarkers) {
                layerMarkers.forEach((marker, key) => {
                    // Supprimer tous les écouteurs d'événements Google Maps
                    if (marker) {
                        google.maps.event.clearInstanceListeners(marker);
                        marker.map = null;
                    }
                });
            }

            // Nettoyer les marqueurs après avoir supprimé les écouteurs
            this.markerDisplayManager.clearMarkers(this.markerLayerName);
        }

        // 2. Nettoyage des disposers MobX pour éviter les fuites mémoire
        this.disposers.forEach(disposer => {
            if (typeof disposer === 'function') {
                disposer();
            }
        });
        this.disposers.clear();

        // 3. Réinitialisation des références
        this.map = null;

        logger.map.debug(`✅ Nettoyage terminé pour ${this.layerName}Store`);
    }
}

export default BaseLayerStore;